import _ from "lodash";
import React, { useState, useEffect, useRef } from "react";
import { SearchList } from "../SearchList/SearchList";
import classes from "./SearchResult.scss";
import classNames from "classnames";

import withLoader from "UIComponents/GqlWithLoader";
import EmptyView from "UIComponents/EmptyView";
import I18nHOC from "UIComponents/I18nHOC";
import { compose, graphql } from "lib/apolloClient";
import { connect } from "react-redux";
import { getGlobalSearchResult } from "../../modules/GlobalSearchBarQueries";
import { getGlobalSearchResultFromCache } from "../../modules/GlobalSearchBarHelper";
import { Button } from "@toddle-edu/ds-web";
import { NoSearchResultsIllustration } from "@toddle-edu/ds-theme";
import ACLStore from "lib/aclStore";

import {
  typesConfig,
  getParseResultMemoized,
  getSearchFilterDataMemoized,
  MODULE_FOR_SORT_OVERRIDE,
  getCourseBasedModulesList,
  adminBasedModules,
  tasksBasedModules,
  homeBasedModules,
  moduleGroupsConfig,
  projectGroupBasedModules,
  getIsAllCurriculumSelected,
  getIsClassSearchV2Enabled,
  insightsBasedModules,
  getIsInsightsSearchV2Enabled,
  searchGroupPriorityOrder,
  checkForExcludedCurriculum,
  getIsStudentUIDSearchEnabled,
} from "../Utils";

import elasticlunr from "elasticlunr";
import { CURRICULUM_TYPE_DP } from "Constants/stringConstants";
import {
  getCurrentCurriculumContextForGlobalSearch,
  getSelectedAcademicYearId,
  getUserCurriculumPrograms,
} from "modules/CommonSelectors";

const style = {
  emptyViewStyle: {
    containerStyle: {
      padding: "unset",
    },
  },
};

const COUNT_OF_FIRST_N_RESULT = 80;

//first clear stop words in our case who , we ,are might be come in Unit plan and LE
elasticlunr.clearStopWords();

// Note: This tokenizer is similar to the default one
// We have just added the additional step of normalizing and removing accents
elasticlunr.tokenizer = function (str) {
  if (!arguments.length || str === null || str === undefined) return [];

  if (Array.isArray(str)) {
    var arr = str.filter(function (token) {
      return token !== null && token !== undefined;
    });

    var out = [];
    arr.forEach(function (item) {
      const normalizedItem = elasticlunr.utils
        .toString(item)
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");

      var tokens = normalizedItem.split(elasticlunr.tokenizer.seperator);
      out = out.concat(tokens);
    });

    return out;
  }

  // For single string inputs: normalize, remove accents, and split
  return str
    .toString()
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .split(elasticlunr.tokenizer.seperator);
};

// Custom separator: Splitting on spaces, hyphens, underscores, parentheses
elasticlunr.tokenizer.seperator = /[\s\-()_]+/;

//initialize instance of elasticlunr
const index = elasticlunr(function () {
  this.addField("title");
  this.addField("category");
  this.addField("acronym");
  this.addField("email");
  this.addField("sourced_id");

  this.setRef("id");

  // remove stemmer from pipeline to allow matching partial words,
  // refer to https://github.com/weixsong/elasticlunr.js/issues/16
  this.pipeline.remove(elasticlunr.stemmer);
});

const addAllDataToIndex = ({ allData }) => {
  _.forEach(allData, data => index.addDoc(data));
};

const addChangedDataToIndex = ({ changedData }) => {
  _.forEach(changedData, data => index.addDoc(data));
};

const getDataMemoizationParams = ({ params, dataKey }) => {
  const data = _.get(params, dataKey, []);
  const changedDataIds = _.map(data, "id");
  return JSON.stringify(changedDataIds);
};

const addAllDataToIndexMemoized = _.memoize(
  params => addAllDataToIndex(params),
  // params => JSON.stringify(params)
  params => getDataMemoizationParams({ params, dataKey: "allData" })
);

const addAllChangedDataToIndexMemoized = _.memoize(
  params => addChangedDataToIndex(params),
  // params => JSON.stringify(params)
  params => getDataMemoizationParams({ params, dataKey: "changedData" })
);

const getGroupTypeByModuleType = moduleType => {
  if (moduleType === typesConfig.LEARNING_COURSE.id) {
    return moduleGroupsConfig?.LEARNING_COURSES_GROUP?.id;
  }
  if (_.includes(getCourseBasedModulesList(), moduleType)) {
    return moduleGroupsConfig?.CLASSES_GROUP?.id;
  }
  if (_.includes(adminBasedModules, moduleType)) {
    return moduleGroupsConfig?.ADMIN_MODULES_GROUP?.id;
  }
  if (_.includes(tasksBasedModules, moduleType)) {
    return moduleGroupsConfig?.TASKS_MODULES_GROUP?.id;
  }
  if (_.includes(homeBasedModules, moduleType)) {
    return moduleGroupsConfig?.HOME_MODULES_GROUP?.id;
  }
  if (_.includes(projectGroupBasedModules, moduleType)) {
    return moduleGroupsConfig?.PROJECT_GROUP_MODULES_GROUP?.id;
  }
  if (_.includes(insightsBasedModules, moduleType)) {
    return moduleGroupsConfig?.INSIGHTS_MODULE_GROUP?.id;
  }
  return moduleType;
};

const categorizeSearchResultsByModuleType = data => {
  const dataWithGroupType = _.map(data, item => ({
    ...item,
    groupType: getGroupTypeByModuleType(item?.type), // Default to original type if not in mapping
  }));

  const groupedByType = _.groupBy(dataWithGroupType, "groupType");

  const sortedWithinGroups = _.mapValues(groupedByType, group =>
    _.orderBy(group, ["score"], ["desc"])
  );

  const sortedGroups = _.orderBy(
    Object.values(sortedWithinGroups),
    group => group[0].score,
    ["desc"]
  );

  const prioritizedGroups = _.sortBy(sortedGroups, group => {
    const groupType = group[0]?.groupType;
    const priorityIndex = _.indexOf(searchGroupPriorityOrder, groupType);
    return priorityIndex >= 0
      ? priorityIndex
      : _.size(searchGroupPriorityOrder);
  });

  return prioritizedGroups;
};

const limitResultsToACertainCount = ({
  searchData,
  totalResultCount = 10,
  resultsPerGroup = 4,
}) => {
  const finalResult = [];
  let resultsAdded = 0;

  _.forEach(searchData, searchResultGroup => {
    const groupSize = _.size(searchResultGroup);
    const remainingResults = totalResultCount - resultsAdded;
    if (remainingResults === 0) {
      return finalResult;
    }
    const resultsToBePushedInThisIteration = Math.min(
      resultsPerGroup,
      groupSize,
      remainingResults
    );

    // keep track of hidden results in each group
    finalResult.push({
      visibleResults: _.slice(
        searchResultGroup,
        0,
        resultsToBePushedInThisIteration
      ),
      hiddenResults: _.slice(
        searchResultGroup,
        resultsToBePushedInThisIteration
      ),
    });

    resultsAdded += resultsToBePushedInThisIteration;
  });

  return finalResult;
};

const refactorClassBasedModules = ({ formattedOtherModuleResults }) => {
  return _.map(formattedOtherModuleResults, moduleWiseSearchResults => {
    if (
      !_.isEqual(
        moduleWiseSearchResults[0]?.groupType,
        moduleGroupsConfig.CLASSES_GROUP.id
      )
    ) {
      return moduleWiseSearchResults;
    } else {
      // Group results of same classes
      const groupedResults = _.groupBy(
        moduleWiseSearchResults,
        result => result?.metadata?.courseId
      );

      // Sort groups based on highest scored result
      const sortedGroups = _.orderBy(
        Object.values(groupedResults),
        group => group[0].score,
        ["desc"]
      );

      // Pick the first result from each group
      return _.map(sortedGroups, group => group[0]);
    }
  });
};

const getFormattedSearchResults = totalData => {
  const formattedOtherModuleResults = categorizeSearchResultsByModuleType(
    totalData
  );

  const searchResults = [];

  if (!_.isEmpty(formattedOtherModuleResults)) {
    if (getIsClassSearchV2Enabled()) {
      const updatedModuleGroups = refactorClassBasedModules({
        formattedOtherModuleResults,
      });
      searchResults.push(...updatedModuleGroups);
    } else {
      searchResults.push(...formattedOtherModuleResults);
    }
  }

  return limitResultsToACertainCount({ searchData: searchResults });
};

const performSearchSortOverride = result => {
  const { type } = result;

  if (_.includes(MODULE_FOR_SORT_OVERRIDE, type)) {
    return 1;
  }
  return;
};

const getSearchGroupTitle = ({ data, t, selectedCurriculum }) => {
  const groupType = _.get(data, "[0].groupType");
  if (moduleGroupsConfig[groupType]) {
    if (
      _.isEqual(groupType, moduleGroupsConfig.PROJECT_GROUP_MODULES_GROUP.id) &&
      _.isEqual(selectedCurriculum?.type, CURRICULUM_TYPE_DP)
    ) {
      return t("common:core_and_other_projects");
    }
    return t(moduleGroupsConfig[groupType]?.title);
  }

  return t(typesConfig[groupType]?.title);
};

const SearchGroup = props => {
  const { data, onSuggestionClick, searchText, t, selectedCurriculum } = props;

  const groupTitle = getSearchGroupTitle({ data, t, selectedCurriculum });

  return (
    <div className={classes.searchResultGroupContainer}>
      <div
        className={classNames(
          "text-body text-textSubtle",
          classes.headingWrapper
        )}
      >
        {groupTitle}
      </div>
      <SearchList
        data={data}
        onSelectItem={onSuggestionClick}
        searchText={searchText}
      />
    </div>
  );
};

const SearchResultsWrapper = props => {
  const {
    searchResults,
    onSuggestionClick,
    term,
    selectedCurriculum,
    t,
  } = props;

  const [searchGroupResults, setSearchGroupResults] = useState(searchResults);
  const shouldShowSeeMoreButton = ACLStore.can("GlobalSearch:SeeMoreResults");

  const prevSearchResults = useRef(searchResults);

  useEffect(() => {
    // This is done to preserve the state of "See More" buttons when searchResults remain same
    if (!_.isEqual(searchResults, prevSearchResults.current)) {
      setSearchGroupResults(searchResults);
      prevSearchResults.current = searchResults;
    }
  }, [searchResults]);

  const handleSeeMoreClick = groupType => () => {
    setSearchGroupResults(prevResults =>
      _.map(prevResults, ({ visibleResults, hiddenResults }) => {
        if (_.get(visibleResults, "[0].groupType") === groupType) {
          return {
            visibleResults: [...visibleResults, ...hiddenResults.slice(0, 4)],
            hiddenResults: _.slice(hiddenResults, 4),
          };
        }
        return { visibleResults, hiddenResults };
      })
    );
  };

  const groupsCount = _.size(searchGroupResults);

  return _.map(searchGroupResults, ({ visibleResults, hiddenResults }) => {
    const groupType = _.get(visibleResults, "[0].groupType");
    return (
      <div key={groupType} className={classes.searchResultsWrapper}>
        <SearchGroup
          data={
            groupsCount > 1
              ? visibleResults
              : [...visibleResults, ...hiddenResults]
          }
          t={t}
          onSuggestionClick={onSuggestionClick}
          searchText={term}
          selectedCurriculum={selectedCurriculum}
        />
        {shouldShowSeeMoreButton &&
        _.size(hiddenResults) > 0 &&
        groupsCount > 1 ? (
          <div className={classes.seeMoreButtonWrapper}>
            <Button
              variant="progressive"
              type="inline"
              dsVersion="2.0"
              onClick={handleSeeMoreClick(groupType)}
              data-elm="search-item"
              tabIndex="-1"
              // test-id is for manipulating onEnter behaviour
              test-id="globalSearch-searchResults-seeMore"
              // testId is for DS to add data-test-id attribute
              testId="globalSearch-searchResults-seeMore"
            >
              {t("common:see_more_noDots")}
            </Button>
          </div>
        ) : null}
      </div>
    );
  });
};

export const SearchResults = props => {
  const {
    suggestions = [],
    localSuggestions = [],
    term,
    onSuggestionClick,
    t,
    selectedCurriculum,
    isAllCurriculumSelected,
    selectedCurriculumProgramTypes,
  } = props;

  //merge data by id if suggestions from local and backend r same
  const mergedData = _.merge(
    _.keyBy(suggestions, "id"),
    _.keyBy(localSuggestions, "id")
  );

  addAllDataToIndexMemoized({ allData: localSuggestions });

  addAllChangedDataToIndexMemoized({ changedData: suggestions });

  const isStudentUIDSearchEnabled = getIsStudentUIDSearchEnabled();

  const searchResult = index.search(term, {
    fields: {
      title: { boost: 2 },
      category: { boost: 1 },
      acronym: { boost: 2 },
      email: { boost: 2 },
      ...(isStudentUIDSearchEnabled
        ? {
            sourced_id: { boost: 2 },
          }
        : {}),
    },
    expand: true,
  });

  const totalData = _.chain(searchResult)
    .reduce((result, data) => {
      const indexedData = mergedData[data.ref];

      const { curriculum, curriculumProgramId } = _.get(
        indexedData,
        "metadata",
        {}
      );
      const moduleType = _.get(indexedData, "type");
      const resultCurriculumId = curriculum || curriculumProgramId;

      if (
        indexedData &&
        (isAllCurriculumSelected ||
          resultCurriculumId == selectedCurriculum?.id ||
          !resultCurriculumId) &&
        checkForExcludedCurriculum({
          selectedCurriculumProgramTypes,
          type: moduleType,
        })
      ) {
        indexedData.score = data.score;
        result.push(indexedData);
      }
      return result;
    }, [])
    .orderBy(result => result.order, "asc")
    .orderBy(result => result.score, "desc")
    .sortBy(result => performSearchSortOverride(result))
    .splice(0, COUNT_OF_FIRST_N_RESULT)
    .value();

  const searchResults = getFormattedSearchResults(totalData);

  return (
    <div className={classes.searchListContainer}>
      {_.isEmpty(searchResults) ? (
        <div
          className={classes.emptyResult}
          data-test-id="globalSearch-searchResults-noResults"
        >
          <EmptyView
            emptyImageUrl={NoSearchResultsIllustration}
            size={"small"}
            emptyHeaderText={`${t("common:no_results")}!`}
            emptyText={t("integration:try_modifying_your_search")}
            {...style.emptyViewStyle}
          />
        </div>
      ) : (
        <SearchResultsWrapper
          searchResults={searchResults}
          onSuggestionClick={onSuggestionClick}
          term={term}
          selectedCurriculum={selectedCurriculum}
          t={t}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { module, term } = ownProps;

  const {
    id: currentCurriculumProgramId,
    type: currentCurriculumProgramType,
  } = getCurrentCurriculumContextForGlobalSearch(state);

  const userCurriculumPrograms = getUserCurriculumPrograms(state);

  const userCurriculumProgramsIds = _.map(userCurriculumPrograms, "id");

  const isAllCurriculumSelected = getIsAllCurriculumSelected(
    currentCurriculumProgramId
  );

  const userCurriculumProgramType = _.map(
    userCurriculumPrograms,
    curriculum => curriculum.type
  );

  const selectedCurriculumProgramTypes = isAllCurriculumSelected
    ? userCurriculumProgramType
    : [currentCurriculumProgramType];

  const curriculumIds = isAllCurriculumSelected
    ? userCurriculumProgramsIds
    : [currentCurriculumProgramId];

  const academicYearSelectedId = getSelectedAcademicYearId(state);

  const filters = getSearchFilterDataMemoized({
    academicYearSelectedId,
    curriculumIds,
    searchText: term,
    module,
  });

  const isInvalidCurriculumId = _.some(curriculumIds, _.isNil);

  return {
    filters,
    term,
    module,
    isInvalidCurriculumId,
    isAllCurriculumSelected,
    selectedCurriculumProgramTypes,
  };
};

export default compose(
  I18nHOC({
    resource: [
      "common",
      "teacherHomePage",
      "classRoom",
      "configuration",
      "attendance",
      "integration",
    ],
  }),
  connect(mapStateToProps, null),
  graphql(getGlobalSearchResult, {
    name: "getGlobalSearchResultQuery",
    options: ({ filters }) => ({
      fetchPolicy: "cache-and-network",
      variables: filters,
    }),
    skip: props => {
      const { module, isInvalidCurriculumId } = props;
      return (
        module == typesConfig.MODULES.id ||
        isInvalidCurriculumId ||
        (module == typesConfig.PLANNING_INSIGHTS_MODULE.id &&
          getIsInsightsSearchV2Enabled())
      );
    },
    props: ({
      getGlobalSearchResultQuery,
      ownProps: { isData, isLoading, filters, t },
    }) => {
      const result = getGlobalSearchResultFromCache({
        filters,
      });

      const queryResult = _.get(result, "platform.platformSearch", []);

      const suggestions = getParseResultMemoized({
        result: queryResult,
        t,
      });

      return {
        suggestions,
        isData: !_.isEmpty(result) && isData,
        isLoading:
          _.includes([1, 2], getGlobalSearchResultQuery["networkStatus"]) ||
          isLoading,
      };
    },
  }),
  withLoader
)(SearchResults);
